import React from 'react';
import { Atom, Circle, Hexagon, Heart, Building2, ArrowRight } from 'lucide-react';
// Custom Molecule icon - circles connected by lines
function MoleculeIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-110">
      <circle cx="6" cy="12" r="3" data-id="element-111" />
      <circle cx="18" cy="6" r="3" data-id="element-112" />
      <circle cx="18" cy="18" r="3" data-id="element-113" />
      <line x1="8.5" y1="10.5" x2="15.5" y2="7.5" data-id="element-114" />
      <line x1="8.5" y1="13.5" x2="15.5" y2="16.5" data-id="element-115" />
    </svg>;
}
// Custom Cell icon - biological cell shape
function CellIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-116">
      <ellipse cx="12" cy="12" rx="9" ry="7" data-id="element-117" />
      <ellipse cx="12" cy="12" rx="3" ry="2" data-id="element-118" />
      <circle cx="7" cy="10" r="1" fill="currentColor" data-id="element-119" />
      <circle cx="16" cy="14" r="1" fill="currentColor" data-id="element-120" />
    </svg>;
}
export function ConceptsOverview() {
  const scrollToDeepDive = () => {
    const element = document.getElementById('concept-deep-dive');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const levels = [{
    name: 'Atom',
    description: 'A single skill, competency, or area of expertise that contributes to meaningful work',
    icon: Atom,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-100'
  }, {
    name: 'Molecule',
    description: 'People with their unique combination of capabilities, and services which require them',
    icon: MoleculeIcon,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-100'
  }, {
    name: 'Cell',
    description: 'A team — a group of people working together toward a shared purpose',
    icon: CellIcon,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-100'
  }, {
    name: 'Organ',
    description: 'A collection of cells (teams) that together perform a critical function',
    icon: Heart,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-100'
  }, {
    name: 'Organism',
    description: 'The complete system of atoms, molecules, cells, and organs working together',
    icon: Building2,
    color: 'text-pink-500',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-100'
  }];
  return <section id="concepts" className="py-16 md:py-24 bg-white" data-id="element-121">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-122">
        <div className="text-center mb-16" data-id="element-123">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-124">
            The ATD Hierarchy
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-125">
            ATD borrows its structure from biology – not as a metaphor for its
            own sake, but because it provides a useful way to reason about
            complex systems.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-126">
          {levels.map(level => <div key={level.name} className="group bg-white rounded-xl shadow-sm border border-slate-100 p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-id="element-127">
              <div className={`w-14 h-14 rounded-lg ${level.bgColor} ${level.borderColor} border flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`} data-id="element-128">
                <level.icon className={`h-8 w-8 ${level.color}`} data-id="element-129" />
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-130">
                {level.name}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed" data-id="element-131">
                {level.description}
              </p>

              <button onClick={scrollToDeepDive} className="inline-flex items-center text-sm font-semibold text-slate-900 hover:text-indigo-600 transition-colors" data-id="element-132">
                Learn More <ArrowRight className="ml-2 h-4 w-4" data-id="element-133" />
              </button>
            </div>)}
        </div>
      </div>
    </section>;
}